import { useCallback, useState } from "react";

import { isAPIError } from "@/core/services";
import { readErrorDetails } from "@/core/utils";
import { inactivatePapelObra } from "./papeis-obra.gateway";
import type { InactivatePapelObraParams } from "./papeis-obra.types";

export const INACTIVATE_PAPEL_OBRA_FORBIDDEN_MESSAGE =
  "Você não tem permissão para inativar papéis na obra.";
export const INACTIVATE_PAPEL_OBRA_CONFLICT_MESSAGE =
  "Não é possível inativar o papel, pois ele está vinculado a uma obra não concluída.";
export const INACTIVATE_PAPEL_OBRA_NOT_FOUND_MESSAGE =
  "O papel não foi encontrado. Atualize a listagem e tente novamente.";
export const INACTIVATE_PAPEL_OBRA_INVALID_MESSAGE = "Justificativa inválida.";
export const INACTIVATE_PAPEL_OBRA_ERROR_MESSAGE =
  "Não foi possível inativar o papel. Tente novamente.";

/**
 * Motivo da recusa: 409 (obra não concluída — RN02), 403, 404, 400 (vai no
 * campo justificativa) ou qualquer outra falha.
 */
export type InactivatePapelObraFailureKind =
  "conflict" | "forbidden" | "notFound" | "invalid" | "error";

export interface InactivatePapelObraFailure {
  ok: false;
  kind: InactivatePapelObraFailureKind;
  message: string;
}

export type InactivatePapelObraResult = { ok: true } | InactivatePapelObraFailure;

function failure(
  kind: InactivatePapelObraFailureKind,
  message: string,
): InactivatePapelObraFailure {
  return { ok: false, kind, message };
}

/** Nunca rejeita: devolve o resultado para o modal decidir onde mostrar o erro. */
export function useInactivatePapelObra() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inactivate = useCallback(
    async (params: InactivatePapelObraParams): Promise<InactivatePapelObraResult> => {
      setIsSubmitting(true);

      try {
        await inactivatePapelObra(params);
        return { ok: true };
      } catch (error) {
        if (!isAPIError(error)) return failure("error", INACTIVATE_PAPEL_OBRA_ERROR_MESSAGE);

        switch (error.status) {
          case 409:
            return failure("conflict", error.message || INACTIVATE_PAPEL_OBRA_CONFLICT_MESSAGE);
          case 403:
            return failure("forbidden", INACTIVATE_PAPEL_OBRA_FORBIDDEN_MESSAGE);
          case 404:
            return failure("notFound", INACTIVATE_PAPEL_OBRA_NOT_FOUND_MESSAGE);
          case 400: {
            const details = readErrorDetails(error.details);
            return failure(
              "invalid",
              details.justificativa || error.message || INACTIVATE_PAPEL_OBRA_INVALID_MESSAGE,
            );
          }
          default:
            return failure("error", INACTIVATE_PAPEL_OBRA_ERROR_MESSAGE);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return { inactivate, isSubmitting };
}
